import React, { useEffect, useState, useRef } from 'react';
import useAuth from '../../../hooks/useAuth';
import SalesTrendingReportService from '../../../services/SalesTrendingReportService';
import GearSpinner from '../../../components/GearSpinner/GearSpinner';
import Loader from '../../../components/Loader/Loader';
import SalesBarChart from '../../../components/Chart/SalesBarChart';
import shareIcon from '../../../assets/images/share-img.png';
import pdfIcon from '../../../assets/images/pdf-img.png';
import xlsIcon from '../../../assets/images/xls-img.png';
import ShareSalesReportModal from '../../../components/Modals/ShareSalesReportModal';
import moment from 'moment';

const TopCustomerSalesTrendTab = ({ active, customers, showDataFor }) => {
    const { user } = useAuth();
    const chartRefs = useRef({}); // Keep refs stable

    const [localCustomers, setLocalCustomers] = useState([]);
    const [showLoader, setShowLoader] = useState(false);
    
    // 1. New State: Track if exporting is allowed
    const [isExportReady, setIsExportReady] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [sharePayload, setSharePayload] = useState(null);

    const salesTrendingReportService = new SalesTrendingReportService();
    const fetchedCache = useRef(new Set()); 

    useEffect(() => {
        setLocalCustomers(customers);
        fetchedCache.current.clear(); 
        // Reset charts when customers change
        chartRefs.current = {}; 
    }, [customers]);
    
    // 2. Effect: Check if all data is loaded to enable the button
    useEffect(() => {
        if (localCustomers.length > 0) {
            // Check if ANY customer is still loading
            const isLoading = localCustomers.some(c => c.is_loader);
            setIsExportReady(!isLoading);
        } else {
            setIsExportReady(false);
        }
    }, [localCustomers]);

    useEffect(() => {
        if (active && localCustomers.length > 0) {
            localCustomers.forEach(customer => {
                if (customer.is_loader && customer.name !== '' && !fetchedCache.current.has(customer.name)) {
                    fetchCustomerSales(customer.name);
                    fetchedCache.current.add(customer.name);
                }
            });
        }
    }, [localCustomers, active]);

    const fetchCustomerSales = (customerName) => {
        var payload = {
            "portal_user_name": user.name,
            "current_month": moment().format('YYYY-MM'),
            "customer": customerName
        };
        
        salesTrendingReportService.getCustomerSales(payload).then((response) => {
            if (response.status === 200 && response.data.status === 'success') {
                setLocalCustomers(prevCustomers => prevCustomers.map(cust => {
                    if (cust.name === customerName) {
                        return { ...cust, is_loader: false, data: response.data.result };
                    }
                    return cust;
                }));
            }
        }).catch((error) => {
            console.error('Fetch Error:', error);
        });
    };

    if (!active) return null;

    const setChartRef = (key) => (el) => {
        if (el) {
            chartRefs.current[key] = el;
        } else {
            // Optional: Cleanup ref if component unmounts
            delete chartRefs.current[key];
        }
    };

    const collectCharts = () => {
        const images = [];
        const keys = Object.keys(chartRefs.current);

        // 3. FIX: Removed "+ 1". We only expect charts for the customers list.
        if (keys.length < localCustomers.length) {
            console.warn(`Charts not ready. Expected: ${localCustomers.length}, Found: ${keys.length}`);
            alert('Charts are still rendering. Please wait a few seconds and try again.');
            return null; // Return null to indicate failure
        }

        keys.forEach((key) => {
            const chart = chartRefs.current[key];
            // Ensure the component has the getImage method
            if (chart && typeof chart.getImage === 'function') {
                const img = chart.getImage();
                if (img) {
                    images.push({ key, image: img });
                }
            }
        });

        return images;
    };

    const buildPayload = (charts) => ({
        charts: charts, 
        tab: 'top-customer',
        meta: {
            portal_user_name: showDataFor,
            generated_at: new Date().toISOString()
        }
    });

    const handleDownloadPDF = async () => {
        // 4. Double check before starting
        if (!isExportReady) return;

        const charts = collectCharts();
        if (!charts) return; // Stop if charts weren't ready

        setShowLoader(true); // Use boolean true/false
        
        try {
            const response = await salesTrendingReportService.exportSalesTrendingPdf(buildPayload(charts));
            
            const blob = response.data; 
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            const formattedDate = moment().format('MM-DD-YYYY_HH-mm');
            const title = `${showDataFor}-netprice-report-${formattedDate}`;
            
            link.href = url;
            link.download = `${title}.pdf`;
            document.body.appendChild(link);
            link.click();
            
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading file:', error);
            alert("Failed to generate PDF. Please try again.");
        } finally {
            setShowLoader(false);
        }
    };

    const handleDownloadExcel = async () => {
        if (!isExportReady) return; // Guard clause

        const charts = collectCharts();
        if (!charts) return;

        setShowLoader(true);

        try {
            salesTrendingReportService.exportSalesTrendingExcel(buildPayload(charts)).then((response) => {
                const blob = response.data;
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                const formattedDate = moment().format('MM/DD/YYYY HH:mm');
                const title = `${showDataFor}-sales-trending-report-${formattedDate}`;
                link.href = url;
                link.download = `${title}.xlsx`;
                link.click();
                window.URL.revokeObjectURL(url);
                setShowLoader(false);

            });
        } catch (error) {
            console.error('Error downloading datasheet:', error);
            setShowLoader(false);
        }
    }

    const handleShareClick = () => {
        const charts = collectCharts();
        if (!charts) return; // Stop if charts not ready

        const payload = buildPayload(charts);
        setSharePayload(payload);
        setShowShareModal(true);
    };

    return (
        <div className="container tab-pane active">
            <div className="row agencyDiv mb-4">
                <div className="col-md-12 text-center">
                    <button className="btn-link-style"
                        onClick={handleShareClick}
                        disabled={!isExportReady} 
                        style={{ 
                            opacity: isExportReady ? 1 : 0.5, 
                            cursor: isExportReady ? 'pointer' : 'not-allowed' 
                        }}
                    >
                        <img src={shareIcon} alt="share"/>Share
                    </button>
                    <button 
                        className="btn-link-style" 
                        onClick={handleDownloadExcel} 
                        disabled={!isExportReady} // Disable until charts are ready
                        style={{ opacity: isExportReady ? 1 : 0.5, cursor: isExportReady ? 'pointer' : 'not-allowed' }}
                    >
                        <img src={xlsIcon} alt="Excel" />Export Excel
                    </button>
                    
                    {/* 5. Disabled State Styling */}
                    <button 
                        className="btn-link-style" 
                        onClick={handleDownloadPDF}
                        disabled={!isExportReady} 
                        style={{ 
                            opacity: isExportReady ? 1 : 0.5, 
                            cursor: isExportReady ? 'pointer' : 'not-allowed' 
                        }}
                    >
                        <img src={pdfIcon} alt="PDF" />Export PDF
                    </button>
                </div>
            </div>

            {Array.from({ length: Math.ceil(localCustomers.length / 2) }).map((_, rowIndex) => {
                const rowCategories = localCustomers.slice(rowIndex * 2, rowIndex * 2 + 2);
                return (
                    <React.Fragment key={rowIndex}>
                        <div className="row">
                            {rowCategories.map((customer, colIndex) => (
                                <div key={colIndex} className="col-md-6">
                                    <div className="techtop-graphdiv">
                                        { (customer.is_loader) ? (
                                            <GearSpinner />
                                        ) : (
                                            <SalesBarChart 
                                                data={customer.data} 
                                                chatTitle={`${customer.name} Sales Trend - $${customer.data.total_sales_formatted} YTD`} 
                                                ref={setChartRef(`customer_${customer.name}`)}
                                            />
                                        ) }
                                    </div>
                                </div>
                            ))}
                        </div>
                        <hr />
                    </React.Fragment>
                );
            })}
            
            {showLoader && (
                <Loader isLoading={showLoader} message="Processing..."/>
            )}
            {sharePayload && (
                <ShareSalesReportModal 
                    payload={sharePayload} 
                    isShow={showShareModal} 
                    onClose={() => setShowShareModal(false)} 
                />
            )}
        </div>
    );
};

export default TopCustomerSalesTrendTab;